package dev.danko.roblox_craft_ai.uikit.components

import dev.danko.roblox_craft_ai.uikit.i18n.LocaleController
import androidx.compose.runtime.Composable
import androidx.compose.runtime.Immutable
import com.varabyte.kobweb.compose.css.FontWeight
import com.varabyte.kobweb.compose.css.Transition
import com.varabyte.kobweb.compose.css.UserSelect
import com.varabyte.kobweb.compose.foundation.layout.Box
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.foundation.layout.Row
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.*
import com.varabyte.kobweb.compose.ui.toAttrs
import com.varabyte.kobweb.silk.components.text.SpanText
import com.varabyte.kobweb.silk.style.CssStyle
import com.varabyte.kobweb.silk.style.base
import com.varabyte.kobweb.silk.style.toModifier
import dev.danko.roblox_craft_ai.shared.model.AssetType
import dev.danko.roblox_craft_ai.shared.model.GenerationStatus
import dev.danko.roblox_craft_ai.uikit.theme.LocalAppTheme
import dev.danko.roblox_craft_ai.uikit.theme.toAppPalette
import org.jetbrains.compose.web.css.DisplayStyle
import org.jetbrains.compose.web.css.LineStyle
import org.jetbrains.compose.web.css.cssRem
import org.jetbrains.compose.web.css.ms
import org.jetbrains.compose.web.css.percent
import org.jetbrains.compose.web.css.px
import org.jetbrains.compose.web.dom.Div

enum class StepStatus {
    PENDING,
    ACTIVE,
    DONE,
    FAILED
}

@Immutable
data class PipelineStep(
    val id: String,
    val label: String,
    val icon: String,
    val status: StepStatus,
    val timeElapsedMs: Long? = null,
    val description: String = ""
)

val PipelineContainerStyle = CssStyle.base {
    val palette = colorMode.toAppPalette()
    Modifier
        .fillMaxWidth()
        .padding(topBottom = 0.75.cssRem, leftRight = 0.75.cssRem)
        .backgroundColor(palette.surface)
        .borderRadius(0.5.cssRem)
        .border(1.px, LineStyle.Solid, palette.border)
}

val StepNodeStyle = CssStyle.base {
    Modifier
        .size(1.75.cssRem)
        .borderRadius(50.percent)
        .display(DisplayStyle.Flex)
        .userSelect(UserSelect.None)
        .transition(
            Transition.of("background-color", 200.ms),
            Transition.of("border-color", 200.ms),
            Transition.of("box-shadow", 200.ms),
            Transition.of("transform", 200.ms)
        )
}

fun createDefaultPipelineSteps(
    type: AssetType,
    overallStatus: GenerationStatus,
    metadata: Map<String, String> = emptyMap()
): List<PipelineStep> {
    val s = LocaleController.strings.pipeline
    return when (type) {
        AssetType.LUAU_SCRIPT -> {
            val isProcessing = overallStatus == GenerationStatus.PROCESSING || overallStatus == GenerationStatus.PENDING
            val isReady = overallStatus == GenerationStatus.READY
            val isFailed = overallStatus == GenerationStatus.FAILED
            val streamStatus = metadata["streamStatus"] ?: ""
            val isValidating = streamStatus.contains("validat")
            val isRetrying = streamStatus.contains("retry")

            listOf(
                PipelineStep(
                    id = "prompt",
                    label = s.prompt,
                    icon = "📝",
                    status = StepStatus.DONE,
                    description = s.luauPromptDescription
                ),
                PipelineStep(
                    id = "llm",
                    label = s.llmStream,
                    icon = "🧠",
                    status = when {
                        isReady -> StepStatus.DONE
                        isProcessing && (isValidating || isRetrying) -> StepStatus.DONE
                        isProcessing -> StepStatus.ACTIVE
                        isFailed -> StepStatus.FAILED
                        else -> StepStatus.PENDING
                    },
                    description = s.llmStreamDescription
                ),
                PipelineStep(
                    id = "validator",
                    label = s.syntaxCheck,
                    icon = "🔍",
                    status = when {
                        isReady -> StepStatus.DONE
                        isProcessing && (isValidating || isRetrying) -> StepStatus.ACTIVE
                        isFailed -> StepStatus.FAILED
                        else -> StepStatus.PENDING
                    },
                    description = s.syntaxCheckDescription
                ),
                PipelineStep(
                    id = "ready",
                    label = s.ready,
                    icon = "✨",
                    status = when {
                        isReady -> StepStatus.DONE
                        isFailed -> StepStatus.FAILED
                        else -> StepStatus.PENDING
                    },
                    description = s.luauReadyDescription
                )
            )
        }
        AssetType.MESH_3D -> {
            val isProcessing = overallStatus == GenerationStatus.PROCESSING || overallStatus == GenerationStatus.PENDING
            val isReady = overallStatus == GenerationStatus.READY
            val isFailed = overallStatus == GenerationStatus.FAILED
            val stage = metadata["meshStage"] ?: if (isReady) "ready" else if (isProcessing) "gen" else "pending"

            listOf(
                PipelineStep(
                    id = "prompt",
                    label = s.prompt,
                    icon = "📝",
                    status = StepStatus.DONE,
                    description = s.meshPromptDescription
                ),
                PipelineStep(
                    id = "mesh_gen",
                    label = s.meshGeneration,
                    icon = "🧊",
                    status = when {
                        isReady -> StepStatus.DONE
                        isProcessing && (stage == "decimate" || stage == "build") -> StepStatus.DONE
                        isProcessing -> StepStatus.ACTIVE
                        isFailed -> StepStatus.FAILED
                        else -> StepStatus.PENDING
                    },
                    description = s.meshGenerationDescription
                ),
                PipelineStep(
                    id = "decimate",
                    label = s.meshTexturing,
                    icon = "📐",
                    status = when {
                        isReady -> StepStatus.DONE
                        isProcessing && stage == "build" -> StepStatus.DONE
                        isProcessing && stage == "decimate" -> StepStatus.ACTIVE
                        isFailed -> StepStatus.FAILED
                        else -> StepStatus.PENDING
                    },
                    description = s.meshTexturingDescription
                ),
                PipelineStep(
                    id = "rbxm_build",
                    label = s.meshExport,
                    icon = "📦",
                    status = when {
                        isReady -> StepStatus.DONE
                        isProcessing && stage == "build" -> StepStatus.ACTIVE
                        isFailed -> StepStatus.FAILED
                        else -> StepStatus.PENDING
                    },
                    description = s.meshExportDescription
                ),
                PipelineStep(
                    id = "ready",
                    label = s.ready,
                    icon = "✨",
                    status = when {
                        isReady -> StepStatus.DONE
                        isFailed -> StepStatus.FAILED
                        else -> StepStatus.PENDING
                    },
                    description = s.meshReadyDescription
                )
            )
        }
        AssetType.ANIMATION -> {
            val isProcessing = overallStatus == GenerationStatus.PROCESSING || overallStatus == GenerationStatus.PENDING
            val isReady = overallStatus == GenerationStatus.READY
            val isFailed = overallStatus == GenerationStatus.FAILED

            listOf(
                PipelineStep(
                    id = "prompt",
                    label = s.prompt,
                    icon = "📝",
                    status = StepStatus.DONE,
                    description = s.animationPromptDescription
                ),
                PipelineStep(
                    id = "motion_ai",
                    label = s.motionAi,
                    icon = "🏃",
                    status = when {
                        isReady -> StepStatus.DONE
                        isProcessing -> StepStatus.ACTIVE
                        isFailed -> StepStatus.FAILED
                        else -> StepStatus.PENDING
                    },
                    description = s.motionAiDescription
                ),
                PipelineStep(
                    id = "r15_rig",
                    label = s.rigging,
                    icon = "🦴",
                    status = when {
                        isReady -> StepStatus.DONE
                        else -> StepStatus.PENDING
                    },
                    description = s.riggingDescription
                ),
                PipelineStep(
                    id = "ready",
                    label = s.ready,
                    icon = "🎬",
                    status = when {
                        isReady -> StepStatus.DONE
                        isFailed -> StepStatus.FAILED
                        else -> StepStatus.PENDING
                    },
                    description = s.animationReadyDescription
                )
            )
        }
        AssetType.ROJO_PROJECT -> {
            val isProcessing = overallStatus == GenerationStatus.PROCESSING || overallStatus == GenerationStatus.PENDING
            val isReady = overallStatus == GenerationStatus.READY
            val isFailed = overallStatus == GenerationStatus.FAILED

            listOf(
                PipelineStep(
                    id = "prompt",
                    label = s.scaffoldTree,
                    icon = "📁",
                    status = StepStatus.DONE,
                    description = s.scaffoldTreeDescription
                ),
                PipelineStep(
                    id = "server_services",
                    label = s.serverServices,
                    icon = "⚙️",
                    status = when {
                        isReady -> StepStatus.DONE
                        isProcessing -> StepStatus.ACTIVE
                        isFailed -> StepStatus.FAILED
                        else -> StepStatus.PENDING
                    },
                    description = s.serverServicesDescription
                ),
                PipelineStep(
                    id = "client_controllers",
                    label = s.clientControllers,
                    icon = "🖥️",
                    status = when {
                        isReady -> StepStatus.DONE
                        isProcessing -> StepStatus.ACTIVE
                        isFailed -> StepStatus.FAILED
                        else -> StepStatus.PENDING
                    },
                    description = s.clientControllersDescription
                ),
                PipelineStep(
                    id = "ready",
                    label = s.rojoReady,
                    icon = "📦",
                    status = when {
                        isReady -> StepStatus.DONE
                        isFailed -> StepStatus.FAILED
                        else -> StepStatus.PENDING
                    },
                    description = s.rojoReadyDescription
                )
            )
        }
    }
}

@Composable
fun StatusPipeline(
    steps: List<PipelineStep>,
    modifier: Modifier = Modifier
) {
    val theme = LocalAppTheme.current

    Div(PipelineContainerStyle.toModifier().then(modifier).toAttrs()) {
        Row(
            Modifier.fillMaxWidth(),
            verticalAlignment = Alignment.CenterVertically
        ) {
            steps.forEachIndexed { index, step ->
                val isLast = index == steps.size - 1

                val stepColor = when (step.status) {
                    StepStatus.DONE -> theme.colors.statusReady
                    StepStatus.ACTIVE -> theme.colors.accent
                    StepStatus.FAILED -> theme.colors.statusFailed
                    StepStatus.PENDING -> theme.colors.textSecondary
                }

                val stepBg = when (step.status) {
                    StepStatus.DONE -> theme.colors.statusReady.toRgb().copy(alpha = 40)
                    StepStatus.ACTIVE -> theme.colors.accent.toRgb().copy(alpha = 40)
                    StepStatus.FAILED -> theme.colors.statusFailed.toRgb().copy(alpha = 40)
                    StepStatus.PENDING -> theme.colors.surfaceElevated
                }

                val nodeIcon = when (step.status) {
                    StepStatus.DONE -> "✓"
                    StepStatus.ACTIVE -> "↻"
                    StepStatus.FAILED -> "✕"
                    StepStatus.PENDING -> "${index + 1}"
                }

                Column(
                    horizontalAlignment = Alignment.CenterHorizontally,
                    modifier = Modifier.gap(0.25.cssRem)
                ) {
                    Box(
                        StepNodeStyle.toModifier()
                            .backgroundColor(stepBg)
                            .border(1.px, LineStyle.Solid, stepColor)
                            .then(
                                if (step.status == StepStatus.ACTIVE) {
                                    Modifier.boxShadow(0.px, 0.px, 0.px, 3.px, theme.colors.accent.toRgb().copy(alpha = 64))
                                } else Modifier
                            ),
                        contentAlignment = Alignment.Center
                    ) {
                        SpanText(
                            nodeIcon,
                            Modifier
                                .color(stepColor)
                                .fontSize(0.75.cssRem)
                                .fontWeight(FontWeight.Bold)
                        )
                    }

                    SpanText(
                        step.label,
                        Modifier
                            .fontSize(0.6875.cssRem)
                            .fontWeight(if (step.status == StepStatus.ACTIVE) FontWeight.Bold else FontWeight.Medium)
                            .color(if (step.status == StepStatus.PENDING) theme.colors.textSecondary else theme.colors.textPrimary)
                    )
                }

                if (!isLast) {
                    val nextStep = steps[index + 1]
                    val lineColor = if (step.status == StepStatus.DONE && nextStep.status != StepStatus.PENDING) {
                        theme.colors.statusReady
                    } else if (step.status == StepStatus.DONE && nextStep.status == StepStatus.ACTIVE) {
                        theme.colors.accent
                    } else {
                        theme.colors.border
                    }

                    Box(
                        Modifier
                            .fillMaxWidth()
                            .weight(1f)
                            .height(2.px)
                            .margin(leftRight = 0.375.cssRem, bottom = 1.cssRem)
                            .backgroundColor(lineColor)
                            .transition(Transition.of("background-color", 200.ms))
                    )
                }
            }
        }
    }
}
